import { RefObject, useEffect, useId, useRef, useState } from 'react';
import { dynamicConfigApi } from '../../services/dynamicConfigApi';
import { DynamicConfigChange } from '../../types/dynamicConfig';
import { formatWhen, maskValue } from './ConfigKeyEditor';

/**
 * #1113 — moves focus into a dialog when it opens, closes it on Escape, and hands focus back to
 * whatever opened it when it closes (the admin portal has no shared modal primitive).
 */
export function useDialogFocus(initial: RefObject<HTMLElement | null>, onEscape: () => void) {
  const escape = useRef(onEscape);
  escape.current = onEscape;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    initial.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') escape.current(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [initial]);
}

interface Row extends DynamicConfigChange { label: string }

/**
 * The change history of one key (Runtime Config) or of every key on a Finance card, newest first.
 * The same `dynamic_config_audit` rows Runtime Config shows. With `maskSensitive`, a sensitive
 * key's values (pilot customer ids) read as "••• (n chars)".
 */
export default function ConfigHistoryDialog({
  title,
  keys,
  labels,
  sensitiveKeys = [],
  maskSensitive = false,
  token,
  onClose,
}: {
  title: string;
  keys: string[];
  /** Readable name per key; a key without one is shown as the raw key. */
  labels?: Record<string, string>;
  sensitiveKeys?: string[];
  maskSensitive?: boolean;
  token: string;
  onClose: () => void;
}) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const keyList = keys.join('\n');

  useDialogFocus(closeRef, onClose);

  useEffect(() => {
    let live = true;
    const list = keyList.split('\n');
    Promise.all(list.map((k) => dynamicConfigApi.history(k, token)))
      .then((perKey) => {
        if (!live) return;
        const merged = perKey.flatMap((changes, i) => (changes ?? []).map((c) => ({
          ...c,
          key: c.key ?? list[i],
          label: labels?.[list[i]] ?? list[i],
        })));
        merged.sort((a, b) => (b.changedAt ?? '').localeCompare(a.changedAt ?? ''));
        setRows(merged);
      })
      .catch((err: Error) => { if (live) setError(err.message); });
    return () => { live = false; };
    // labels is a render-time lookup; re-fetching on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyList, token]);

  const multi = keys.length > 1;
  const show = (key: string, v: string | null) => {
    if (v === null) return <em>deployed value</em>;
    return maskSensitive && sensitiveKeys.includes(key) ? maskValue(v) : v;
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal admin-modal-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
        data-testid="config-history-dialog"
      >
        <div className="admin-modal-body">
          <h2 id={titleId}>{title}</h2>
          {!multi && <p className="admin-page-subtitle"><code>{keys[0]}</code></p>}

          {error && <div className="admin-error" role="alert">{error}</div>}
          {!error && rows === null && <div className="admin-loading">Loading…</div>}
          {rows !== null && rows.length === 0 && (
            <p className="admin-page-subtitle">This setting has never been changed here.</p>
          )}
          {rows !== null && rows.length > 0 && (
            <div className="config-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Who</th>
                    {multi && <th scope="col">Setting</th>}
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((change, i) => (
                    <tr key={`${change.key}-${change.changedAt}-${i}`}>
                      <td style={{ whiteSpace: 'nowrap' }}>{formatWhen(change.changedAt)}</td>
                      <td>{change.actorName ?? change.actorSubjectId ?? 'unknown'}</td>
                      {multi && <td>{change.label}</td>}
                      <td>{show(change.key, change.oldValue)}</td>
                      <td>{show(change.key, change.newValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {maskSensitive && sensitiveKeys.some((k) => keys.includes(k)) && (
            <p className="config-hint" style={{ marginTop: '0.5rem' }}>
              The same audit rows as Runtime Config. Pilot customer ids are masked.
            </p>
          )}
        </div>

        <div className="admin-modal-footer">
          <button ref={closeRef} type="button" className="admin-btn admin-btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
